import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink, Copy, HardDrive, Calendar, Hash } from "lucide-react";
import type { SearchHit } from "../lib/api";

interface DetailPanelProps {
  hit: SearchHit | null;
  onClose: () => void;
}

function formatBytes(n: number): string {
  if (!isFinite(n) || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDate(t: number): string {
  if (!t) return "—";
  const ms = t < 1e12 ? t * 1000 : t;
  return new Date(ms).toLocaleString();
}

export default function DetailPanel({ hit, onClose }: DetailPanelProps) {
  return (
    <AnimatePresence>
      {hit && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
            onClick={onClose}
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
            className="fixed top-0 right-0 bottom-0 w-full md:w-[520px] z-50 glass-prominent ring-glass !rounded-none md:!rounded-l-2xl border-l border-white/[0.10] flex flex-col"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
              <div className="min-w-0">
                <div className="eyebrow">Document</div>
                <h2 className="mt-1 text-lg font-semibold text-slate-100 truncate">
                  {hit.filename}
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 rounded-lg border border-white/[0.06] hover:bg-white/[0.05] flex items-center justify-center text-slate-400 hover:text-slate-200"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="glass-subtle p-3">
                  <div className="eyebrow flex items-center gap-1.5">
                    <HardDrive size={10} /> Machine
                  </div>
                  <div className="mt-1 text-slate-200 font-medium">{hit.machine || "—"}</div>
                </div>
                <div className="glass-subtle p-3">
                  <div className="eyebrow flex items-center gap-1.5">
                    <Calendar size={10} /> Modified
                  </div>
                  <div className="mt-1 text-slate-200 font-medium">{formatDate(hit.mtime)}</div>
                </div>
                <div className="glass-subtle p-3">
                  <div className="eyebrow">Size</div>
                  <div className="mt-1 text-slate-200 font-medium">{formatBytes(hit.size || 0)}</div>
                </div>
                <div className="glass-subtle p-3">
                  <div className="eyebrow">Score</div>
                  <div className="mt-1 text-slate-200 font-medium">
                    {((hit.score ?? 0) * 100).toFixed(0)}%
                  </div>
                </div>
              </div>

              {hit.path && (
                <div>
                  <div className="eyebrow mb-2">Path</div>
                  <div className="glass-subtle p-3 font-mono text-xs text-slate-300 break-all flex items-center justify-between gap-2">
                    <span>{hit.path}</span>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(hit.path)}
                      className="text-slate-500 hover:text-slate-200 shrink-0"
                      aria-label="Copy path"
                    >
                      <Copy size={12} />
                    </button>
                  </div>
                </div>
              )}

              {hit.sha256 && (
                <div>
                  <div className="eyebrow mb-2 flex items-center gap-1.5">
                    <Hash size={10} /> sha256
                  </div>
                  <div className="glass-subtle p-3 font-mono text-[11px] text-slate-400 break-all">
                    {hit.sha256}
                  </div>
                </div>
              )}

              {hit.preview && (
                <div>
                  <div className="eyebrow mb-2">Preview</div>
                  <div className="glass-subtle p-4 text-sm leading-relaxed text-slate-300 whitespace-pre-wrap max-h-72 overflow-y-auto">
                    {hit.preview}
                  </div>
                </div>
              )}

              {hit.duplicates && hit.duplicates.length > 0 && (
                <div>
                  <div className="eyebrow mb-2">
                    Duplicates ({hit.duplicates.length})
                  </div>
                  <div className="space-y-1.5">
                    {hit.duplicates.map((d, i) => (
                      <div
                        key={i}
                        className="glass-subtle px-3 py-2 font-mono text-[11px] text-slate-400 break-all"
                      >
                        {d}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {hit.blob_url && (
              <div className="px-5 py-4 border-t border-white/[0.06]">
                <a
                  href={hit.blob_url}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-accent-blue/15 border border-accent-blue/40 text-accent-blue hover:bg-accent-blue/25 text-sm font-medium"
                >
                  <ExternalLink size={14} />
                  Open blob URL
                </a>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
